/**
 * ChatErrorCode mirrors the daemon's `src/errors.ts` codes and adds two the
 * frontend derives from transport state: `server_error` (5xx) and `network`
 * (the fetch itself threw). `unauthorized` / `forbidden` are handled by their
 * own flows and never reach this module.
 *
 * `branch_exists`, `branch_shared` and `rename_failed` come from one route
 * rather than `src/errors.ts`: the branch rename in PATCH
 * /conversations/:project/:id.
 * They live here anyway because an unlisted code falls through to `unknown`,
 * whose body is "a request failed (HTTP 400)" — which would throw away the only
 * sentence that says what actually went wrong.
 */
export type ChatErrorCode =
  | "in_flight"
  | "daily_cap"
  | "monthly_cap"
  | "rate_limited"
  | "overloaded"
  | "timeout"
  | "unknown_conversation"
  | "at_capacity"
  | "worktree_failed"
  | "spawn_timeout"
  | "credits_exhausted"
  | "deep_mode_limit"
  | "context_exhausted"
  | "subagent_write_blocked"
  | "subagent_guard_failed"
  | "image_rejected"
  | "queue_full"
  | "message_too_large"
  | "too_many_waiters"
  | "spend_reserved"
  | "account_paused"
  | "branch_exists"
  | "branch_shared"
  | "rename_failed"
  | "server_error"
  | "network"
  | "unknown";

export type FriendlyError = {
  code: ChatErrorCode;
  title: string;
  body: string;
  hint?: string;
  severity: "info" | "warn" | "error";
  retryable: boolean;
};

const KNOWN: ReadonlySet<string> = new Set([
  "in_flight",
  "daily_cap",
  "monthly_cap",
  "rate_limited",
  "overloaded",
  "timeout",
  "unknown_conversation",
  "at_capacity",
  "worktree_failed",
  "spawn_timeout",
  "credits_exhausted",
  "deep_mode_limit",
  "context_exhausted",
  "subagent_write_blocked",
  "subagent_guard_failed",
  "image_rejected",
  "queue_full",
  "message_too_large",
  "too_many_waiters",
  "spend_reserved",
  "account_paused",
  "branch_exists",
  "branch_shared",
  "rename_failed",
  "server_error",
  "network",
  "unknown",
]);

export function friendlyError(input: { status?: number; code?: string; rawMessage?: string }): FriendlyError {
  const code = deriveCode(input);
  const raw = input.rawMessage?.trim();
  switch (code) {
    case "in_flight":
      return {
        code,
        title: "Still working",
        body: "ThunderAy AI is still finishing your previous message. Wait for it, or press Stop and resend.",
        severity: "info",
        retryable: false,
      };
    case "daily_cap":
      return {
        code,
        title: "Daily limit reached",
        body: raw || "You have reached today's usage limit. It resets tomorrow.",
        severity: "warn",
        retryable: false,
      };
    case "monthly_cap":
      return {
        code,
        title: "Monthly limit reached",
        body: raw || "You have reached this month's usage limit. It resets next month.",
        severity: "warn",
        retryable: false,
      };
    case "rate_limited":
      return {
        code,
        title: "ThunderAy AI is busy",
        body: "Too many requests right now. Wait a few seconds and send it again.",
        severity: "warn",
        retryable: true,
      };
    case "overloaded":
      return {
        code,
        title: "ThunderAy AI is busy",
        body: "The model is overloaded right now. Wait a few seconds and send it again.",
        severity: "warn",
        retryable: true,
      };
    case "timeout":
      return {
        code,
        title: "Response timed out",
        body: "That one took too long and was stopped. Try again, or send a shorter prompt.",
        severity: "warn",
        retryable: true,
      };
    case "at_capacity":
      return {
        code,
        title: "Waiting for a slot",
        body: "You already have a few conversations streaming. This one will start as soon as one frees up.",
        severity: "info",
        retryable: true,
      };
    case "unknown_conversation":
      return {
        code,
        title: "Conversation not found",
        body: "That conversation is gone — it may have been archived; pick another or start a new one.",
        severity: "warn",
        retryable: false,
      };
    case "worktree_failed":
      return {
        code,
        title: "Could not prepare a branch",
        body: "Could not prepare a working branch for this conversation. Try again.",
        severity: "error",
        retryable: true,
      };
    case "spawn_timeout":
      // The daemon gave up starting the session and handed the conversation
      // back, so the member is free to send again immediately. NOT retryable:
      // the user turn is not recorded until after the spawn, so there is no
      // stored message for an automatic resend to repeat, and resending would
      // have to invent one. Asking is the only honest option, and it is a
      // single tap. NO_AUTO_CONTINUE_CODES in useChatStream is the other brake.
      return {
        code,
        title: "Could not start",
        body: "The workspace could not start this turn in time. Send it again.",
        severity: "warn",
        retryable: false,
      };
    case "credits_exhausted":
      // Deliberately money-free and non-revealing: members must never learn
      // usage is metered. NOT retryable — auto-continuing here would spend on
      // the personal fallback credential.
      return {
        code,
        title: "Temporarily unavailable",
        body: "ThunderAy AI is temporarily unavailable right now. Please try again in a little while.",
        severity: "warn",
        retryable: false,
      };
    case "deep_mode_limit":
      // Money-free and non-revealing, like credits_exhausted: never expose that
      // spend is metered, or that a panel size / cost tripped a guard. NOT
      // retryable — the turn was deliberately stopped, so auto-continuing would
      // just re-trip it.
      return {
        code,
        title: "Response stopped",
        body: "That turn was doing too much at once and was stopped. Try a more focused request, or break it into smaller steps.",
        severity: "warn",
        retryable: false,
      };
    case "context_exhausted":
      // A hard ceiling, not a hiccup. `classifyUpstream` in the daemon's
      // src/errors.ts raises this for autocompact thrash, a prompt already over
      // length, and a 413. Every one of those is a property of the
      // conversation rather than of the moment, so the same turn re-run refills
      // the same window and dies identically. NOT retryable, and that flag is
      // load-bearing: MAX_AUTO_CONTINUE fired two more attempts into that same
      // full window, turning one long failure into three, and Message.tsx:338
      // hides the card while `resuming` is true so the user only ever saw the
      // last of them.
      //
      // The body names the remedy because nothing about a dead turn suggests
      // it. It deliberately does NOT relay the daemon's own sentence, which is
      // written for an operator reading a log and points at an oversized file
      // read the member never asked for.
      //
      // "on the same branch" is the difference between a remedy and a dead end.
      // A new conversation with the Branch field blank is auto-named a fresh
      // branch (src/server.ts:2570), so it opens on the base branch with none
      // of the member's work visible. Reusing the branch name, which
      // ConversationDrawer offers from a datalist of existing branches, gives
      // the new conversation its OWN worktree checked out at the same commit
      // (src/git/worktrees.ts:388-401), so committed work carries over and
      // nothing is destroyed. Uncommitted work stays behind in the old
      // worktree, which does not change the instruction.
      return {
        code,
        title: "This conversation ran out of room",
        body: "This conversation has grown too long to continue. Start a new conversation on the same branch and say where you left off to pick the work back up.",
        hint: "Large files and long command outputs fill a conversation quickly, so asking for just the part you need makes one last longer.",
        severity: "warn",
        retryable: false,
      };
    case "subagent_guard_failed":
      // The workspace could not ENFORCE its sub-agent read-only rule this turn:
      // the guard that denies sub-agent writes failed to run (it timed out,
      // crashed, or could not be found). Measured: a PreToolUse hook that
      // cannot run does not block the call, it lets it through — so carrying on
      // would mean running a panel with no guard at all over a shared working
      // tree. Stopping is the safe answer, and unlike the money guards this one
      // is safe to explain, because it is an operational fault and not
      // something the member did. Retrying is genuinely worth it: the fault is
      // usually transient (a loaded host makes the hook time out), which is why
      // the copy asks for exactly that. Still NOT auto-retryable — a resend
      // should be the member's decision, not a loop.
      return {
        code,
        title: "Response stopped",
        body: "The workspace could not enforce its sub-agent safety rule for this turn. Send the message again.",
        hint: "Nothing was lost. If it keeps happening, the workspace needs a look.",
        severity: "warn",
        retryable: false,
      };
    case "subagent_write_blocked":
      // A parallel sub-agent tried to modify files and the daemon stopped the
      // turn. Unlike the money guards this one is SAFE to explain: the rule is
      // a product rule, not a spend one, and saying so is the difference
      // between a user who can adjust the request and one who cannot. Still no
      // command and no file path — the daemon keeps those in its log. NOT
      // retryable: the same request fans out the same way and re-trips it.
      return {
        code,
        title: "Response stopped",
        body: "A parallel sub-agent tried to modify files, so the turn was stopped. Sub-agents are read-only — only the main agent changes files.",
        hint: "Ask for the change directly, without a parallel review panel.",
        severity: "warn",
        retryable: false,
      };
    case "queue_full":
      // 429 on an enqueue: this chat's queue (or all of the member's queues
      // together) is at its cap. NOT retryable until a prompt runs or is removed.
      return {
        code,
        title: "Queue is full",
        body: "Too many prompts are waiting. Wait for one to run, or remove one, then add this again.",
        severity: "warn",
        retryable: false,
      };
    case "message_too_large":
      // 413 on /chat, an enqueue or an edit. Resending the same text cannot help.
      return {
        code,
        title: "Message too long",
        body: "That message is too long to send. Shorten it, or split it into smaller parts.",
        severity: "warn",
        retryable: false,
      };
    case "too_many_waiters":
      // 429 on the queue wait. The hook backs off and asks again by itself, so
      // this card only shows if it ever reaches a banner.
      return {
        code,
        title: "Too many open tabs",
        body: "This chat is open in too many tabs. Close one and it will carry on.",
        severity: "info",
        retryable: false,
      };
    case "spend_reserved":
      // An unattended start's refusal (the queue waits on it). /chat no longer
      // answers it, but an older daemon did, so the copy stays. Money-free and
      // non-revealing like credits_exhausted: the member must never learn
      // usage is metered. NOT retryable, so it is never auto-continued
      // (NO_AUTO_CONTINUE_CODES is the other brake).
      return {
        code,
        title: "Please wait",
        body: "Your other chats are still working. Try this one again when one of them finishes.",
        severity: "warn",
        retryable: false,
      };
    case "account_paused":
      // 409 on a queue Resume: an account-wide wall still holds. The hook shows
      // the queue's paused banner with the wall's reason instead of this card.
      return {
        code,
        title: "Queue still paused",
        body: "ThunderAy AI is temporarily unavailable right now. The queue can be resumed in a little while.",
        severity: "warn",
        retryable: false,
      };
    case "image_rejected":
      // The daemon refused the attachment itself (too many, too large,
      // unsupported type, undecodable data). NOT retryable: nothing about
      // resending changes the verdict, and an automatic continue would bill a
      // turn for a request that cannot succeed. `raw` is the daemon's own
      // reason and names WHICH cap was hit, so it is shown as-is rather than
      // flattened into copy that would leave the user guessing.
      return {
        code,
        title: "Image not attached",
        body: raw || "That image could not be attached. Try a smaller image, or fewer of them.",
        severity: "warn",
        retryable: false,
      };
    case "branch_exists":
      // `raw` is the daemon's sentence and it NAMES the branch that collided
      // ("branch ai/x already exists"); canned copy would leave the user
      // guessing which one. Not retryable — the same name collides forever, so
      // only a different name changes the answer, which is what `hint` says.
      return {
        code,
        title: "Branch already exists",
        body: raw || "A branch with that name already exists.",
        hint: "Pick a different name.",
        severity: "warn",
        retryable: false,
      };
    case "branch_shared":
      // Several conversations share this branch, and a git rename moves HEAD in
      // every worktree checked out on it — so the daemon refuses rather than
      // drag the others somewhere their rows do not know about. `raw` counts
      // the sharers; canned copy would leave the user wondering how many are in
      // the way. Not retryable: nothing about resending frees the branch, so
      // `hint` carries the only thing that does.
      return {
        code,
        title: "Branch is shared",
        body: raw || "Other conversations are open on this branch, so it cannot be renamed.",
        hint: "Archive the others first, or rename from the conversation once it is the only one left.",
        severity: "warn",
        retryable: false,
      };
    case "rename_failed":
      // `raw` is git's own stderr, verbatim: the only thing that says WHY the
      // rename failed. It is neither short nor well-formed (multi-line, quotes,
      // angle brackets), so every renderer must treat it as text.
      return {
        code,
        title: "Could not rename the branch",
        body: raw || "Git refused the rename. Check the name and try again.",
        severity: "error",
        retryable: false,
      };
    case "server_error":
      return {
        code,
        title: "Something went wrong",
        body: "The server hit an error. Try again in a moment.",
        severity: "error",
        retryable: true,
      };
    case "network":
      return {
        code,
        title: "Connection lost",
        body: "Could not reach ThunderAy AI. Check your connection and try again.",
        severity: "error",
        retryable: true,
      };
    default:
      return {
        code: "unknown",
        title: "Something went wrong",
        // `raw` is the daemon's own sentence and it reached the browser intact.
        // Six sibling branches already prefer it; this one discarded it, which
        // is why a real explanation rendered as "A request failed." It stays
        // `||` rather than `??` because `raw` is trimmed, so a whitespace-only
        // message is "" and must fall back instead of rendering a blank card.
        body: raw || unknownBody(input.status),
        severity: "error",
        retryable: true,
      };
  }
}

function deriveCode(input: { status?: number; code?: string; rawMessage?: string }): ChatErrorCode {
  if (input.code && KNOWN.has(input.code)) return input.code as ChatErrorCode;
  if ((input.rawMessage ?? "").toLowerCase().startsWith("network error")) return "network";
  const s = input.status;
  if (s === 429) return "rate_limited";
  if (typeof s === "number" && s >= 500) return "server_error";
  return "unknown";
}

function unknownBody(status?: number): string {
  return typeof status === "number"
    ? `A request failed (HTTP ${status}). Try again in a moment.`
    : "A request failed. Try again in a moment.";
}
